import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../services/API/api.service';
import { AlertService } from '../../services/Alert/alert.service';

// Add interfaces for better type safety
interface TopicSize {
  id: number;
  name: string;
  price: number;
}

interface Topic {
  id: number;
  name: string;
  price: number;
  sizes: TopicSize[];
  avatar: string;
}

interface CartCategory {
  id: number;
  name: string;
  default_size: number | null;
  has_sizes: number;
  default_price: number;
  avatar: string;
  selected_size?: {
    id: number;
    name: string;
    price: number;
  };
}

@Component({
  selector: 'app-custom-food',
  templateUrl: './custom-food.component.html',
  styleUrls: ['./custom-food.component.scss']
})
export class CustomFoodComponent implements OnInit {
  categories: any[] = [];
  subCategories: any[] = [];
  topics: Topic[] = []; // Current topics for the selected subcategory
  selectedCategory: any = null;
  selectedSubCategory: any = null;
  selectedIngredients: any[] = [];
  loading: boolean = false;

  allTopicsMap: Map<number, Topic> = new Map(); // Store all topics by ID permanently
  topicSizeMap: Map<string, Map<number, number>> = new Map(); // Store size prices by topic ID

  // Size properties
  selectedSize: any = null;
  
  // Product quantity
  productQuantity: number = 1;
  
  // Half & Half properties
  isHalfAndHalfMode: boolean = false;
  selectedHalf: string = 'whole'; // 'left', 'right', or 'whole'

  // Quantity input for selected ingredient
  showQuantityInput: boolean = false;
  selectedIngredientForQuantity: Topic | null = null;
  quantity: number = 1;
  quantityHalf: string = 'whole';

  constructor(private apiService: ApiService, private alert: AlertService) { }

  ngOnInit(): void {
    this.loadCategories();
  }

  loadCategories(): void {
    this.loading = true;
    this.apiService.getFoodCategories().subscribe({
      next: (response) => {
        this.categories = response.data || [];
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading categories:', error);
        this.loading = false;
      }
    });
  }

  selectSize(size: any): void {
    this.selectedSize = size;
    
    // When size changes, we need to update ingredient prices
    // This will affect the display in the ingredients list
    // No need to reload topics, just refresh the UI
  }

  // Get the appropriate price for a topic based on selected size
  getTopicPrice(topic: Topic): number {
    if (!this.selectedSize) {
      return topic.price; // Return default price if no size selected
    }
    
    // Check if the topic has size-specific pricing
    if (topic.sizes && topic.sizes.length > 0) {
      const sizePrice = topic.sizes.find(s => s.id === this.selectedSize.id);
      if (sizePrice) {
        return sizePrice.price;
      }
    }
    
    // Return default price if no size-specific price found
    return topic.price;
  }

  // Toggle half&half mode
  toggleHalfAndHalfMode(): void {
    this.isHalfAndHalfMode = !this.isHalfAndHalfMode;
    this.selectedIngredients = []; // Clear ingredients when toggling mode
    
    if (this.isHalfAndHalfMode) {
      this.selectedHalf = 'left';
    } else {
      this.selectedHalf = 'whole';
    }
  }

  loadSubCategories(categoryId: number): void {
    this.loading = true;
    this.apiService.getFoodSubCategories(categoryId).subscribe({
      next: (response) => {
        this.subCategories = response.data || [];
        this.loading = false;
        
        // Auto-select first subcategory if available
        if (this.subCategories.length > 0) {
          this.selectSubCategory(this.subCategories[0]);
        }
      },
      error: (error) => {
        console.error('Error loading subcategories:', error);
        this.loading = false;
      }
    });
  }

  selectSubCategory(subCategory: any): void {
    this.selectedSubCategory = subCategory;
    this.hideQuantityInput();
    this.loadTopics(this.selectedCategory.id, subCategory.id);
  }

  loadTopics(categoryId: number, subCategoryId: number): void {
    this.loading = true;
    this.apiService.getFoodTopics(categoryId, subCategoryId).subscribe({
      next: (response) => {
        const newTopics = response.data || [];
        
        // Store each topic in the permanent map
        newTopics.forEach((topic: Topic) => {
          // Store the full topic in the map
          this.allTopicsMap.set(topic.id, topic);
          
          // Also store size prices in a separate map for quick access
          if (topic.sizes && topic.sizes.length > 0) {
            const sizePriceMap = new Map<number, number>();
            topic.sizes.forEach(size => {
              sizePriceMap.set(size.id, size.price);
            });
            this.topicSizeMap.set(`topic_${topic.id}`, sizePriceMap);
          }
        });
        
        // Update current topics for display
        this.topics = newTopics;
        this.loading = false;
        
        // console.log('All topics map:', this.allTopicsMap);
        // console.log('Current topics:', this.topics);
      },
      error: (error) => {
        console.error('Error loading topics:', error);
        this.loading = false;
      }
    });
  }

  toggleIngredient(topic: Topic): void {
    this.hideQuantityInput();
    
    const currentHalf = this.isHalfAndHalfMode ? this.selectedHalf : 'whole';
    const currentPrice = this.getTopicPrice(topic);
    
    const existingIndex = this.selectedIngredients.findIndex(
        ing => ing.id === topic.id && ing.half === currentHalf
    );
    
    if (existingIndex > -1) {
        this.selectedIngredients.splice(existingIndex, 1);
    } else {
        // Store the original topic data for size reference
        const ingredientWithHalf = {
            id: topic.id,
            name: topic.name,
            price: currentPrice, // Current price at time of adding
            originalPrice: topic.price, // Keep original for reference
            originalSizes: topic.sizes, // Store the sizes array for reference
            avatar: topic.avatar,
            half: currentHalf,
            quantity: 1,
            sizeId: this.selectedSize?.id || null
        };
        this.selectedIngredients.push(ingredientWithHalf);
    }
}

// Add multiple quantities of the same ingredient
  addMultipleIngredients(): void {
    if (this.quantity < 1 || !this.selectedIngredientForQuantity) return;

    const topic = this.selectedIngredientForQuantity;
    const currentHalf = this.quantityHalf;
    const currentPrice = this.getTopicPrice(topic);

    const existingIndex = this.selectedIngredients.findIndex(
        ing => ing.id === topic.id && ing.half === currentHalf
    );

    if (existingIndex > -1) {
        this.selectedIngredients[existingIndex].quantity = this.quantity;
        // Update price but keep original data
        this.selectedIngredients[existingIndex].price = currentPrice;
        this.selectedIngredients[existingIndex].sizeId = this.selectedSize?.id || null;
    } else {
        const ingredientWithHalf = {
            id: topic.id,
            name: topic.name,
            price: currentPrice,
            originalPrice: topic.price,
            originalSizes: topic.sizes, // Store the sizes array
            avatar: topic.avatar,
            half: currentHalf,
            quantity: this.quantity,
            sizeId: this.selectedSize?.id || null
        };
        this.selectedIngredients.push(ingredientWithHalf);
    }

    this.hideQuantityInput();
  }


  // Show quantity input for adding multiple of the same ingredient
  showAddMultiple(topic: Topic): void {
    this.selectedIngredientForQuantity = topic;
    this.quantity = this.getIngredientQuantity(topic.id, this.isHalfAndHalfMode ? this.selectedHalf : 'whole');
    this.quantityHalf = this.isHalfAndHalfMode ? this.selectedHalf : 'whole';
    this.showQuantityInput = true;
  }

  // Hide quantity input
  hideQuantityInput(): void {
    this.showQuantityInput = false;
    this.selectedIngredientForQuantity = null;
    this.quantity = 1;
  }

  // Check if ingredient is selected
  isIngredientSelected(ingredientId: number): boolean {
    const currentHalf = this.isHalfAndHalfMode ? this.selectedHalf : 'whole';
    return this.selectedIngredients.some(
      ing => ing.id === ingredientId && ing.half === currentHalf
    );
  }

  // Get ingredient quantity for display
  getIngredientQuantity(ingredientId: number, half: string): number {
    const ingredient = this.selectedIngredients.find(
      ing => ing.id === ingredientId && ing.half === half
    );
    return ingredient ? ingredient.quantity || 1 : 0;
  }

  // Remove ingredient
  removeIngredient(ingredientId: number, half?: string): void {
    if (!this.isHalfAndHalfMode) {
      this.selectedIngredients = this.selectedIngredients.filter(ing => ing.id !== ingredientId);
    } else {
      const targetHalf = half || this.selectedHalf;
      this.selectedIngredients = this.selectedIngredients.filter(
        ing => !(ing.id === ingredientId && ing.half === targetHalf)
      );
    }
  }

  getIngredientsByHalf(half: string): any[] {
    return this.selectedIngredients.filter(ing => ing.half === half);
  }

  // Get base price (considering size)
  getBasePrice(): number {
    if (this.selectedSize) {
      return this.selectedSize.price;
    }
    return this.selectedCategory?.default_price || 0;
  }

  // Calculate grand total (item price * quantity)
  calculateGrandTotal(): number {
    return this.calculateItemPrice() * this.productQuantity;
  }

  cartLoading: boolean = false;
  addToCart(): void {
    this.cartLoading = true;
    // Validate size selection if category has sizes
    if (this.selectedCategory.has_sizes && !this.selectedSize) {
      this.alert.danger('Please select a size before adding to cart.');
      this.cartLoading = false;
      return;
    }

    const itemPrice = this.calculateItemPrice();

    // Prepare ingredients with proper total price calculation
    const ingredients = this.selectedIngredients.map(ing => {
      let totalPrice: number;
      
      if (this.isHalfAndHalfMode) {
        if (ing.half === 'whole') {
          totalPrice = ing.price * ing.quantity;
        } else {
          // Half price for left/right halves
          totalPrice = (ing.price * ing.quantity);
        }
      } else {
        totalPrice = ing.price * ing.quantity;
      }
      
      return {
        id: ing.id,
        name: ing.name,
        price: ing.price,
        quantity: ing.quantity || 1,
        half: ing.half,
        sizeId: ing.sizeId, // Include which size was used for this ingredient
        totalPrice: parseFloat(totalPrice.toFixed(2))
      };
    });

    // Prepare category object with size information
    const category: CartCategory = {
      id: this.selectedCategory.id,
      name: this.selectedCategory.name,
      default_size: this.selectedSize?.id || null,
      has_sizes: this.selectedCategory.has_sizes,
      default_price: this.selectedCategory.default_price,
      avatar: this.selectedCategory.avatar
    };

    // Add size info if selected
    if (this.selectedSize) {
      category.selected_size = {
        id: this.selectedSize.id,
        name: this.selectedSize.name,
        price: this.selectedSize.price
      };
    }

    const customizedProduct = {
      customProducts: {
        category: category,
        subCategory: {
          id: this.selectedSubCategory.id,
          name: this.selectedSubCategory.name,
          avatar: this.selectedSubCategory.avatar
        },
        ingredients: ingredients,
        isHalfAndHalf: this.isHalfAndHalfMode,
        totalPrice: parseFloat(itemPrice.toFixed(2))
      },
      quantity: this.productQuantity
    };
    
    console.log('Cart Item:', customizedProduct);

    const guestMode = sessionStorage.getItem('guestMode');
    const token = localStorage.getItem('fontana_token')?.toString();

    if(customizedProduct){
      if (token) {
        this.apiService.addToCart(customizedProduct).subscribe({
          next: (response) => {
            console.log('Added to cart:', response);
            this.cartLoading = false;
            this.alert.success('Added to cart!');
          },
          error: (error) => {
            console.error('Error adding to cart:', error);
            this.cartLoading = false;
            this.alert.danger('Error adding to cart!');
          }
        })
      }else if (guestMode) {
        this.handleGuestCart();
      } else {
        this.alert.danger('You must be logged in or <a (click)="enableGuestMode()">continue as a guest</a> to add products to your cart');
        this.cartLoading = false;
      }
    }else{
      this.cartLoading = false;
    }
  }
  
  private handleGuestCart(): void {
    try {
      // Get existing cart items from sessionStorage or initialize empty array
      let cartItems: any[] = [];
      const storedCart = sessionStorage.getItem('customCartItems');
      
      if (storedCart) {
        try {
          cartItems = JSON.parse(storedCart);
          if (!Array.isArray(cartItems)) {
            cartItems = []; // Reset if corrupted data
          }
        } catch (e) {
          console.error('Error parsing cart items', e);
          cartItems = [];
        }
      }

      // Calculate item price
      const itemPrice = this.calculateItemPrice();

      // Prepare ingredients with proper total price calculation
      const ingredients = this.selectedIngredients.map(ing => {
        let totalPrice: number;
        
        if (this.isHalfAndHalfMode) {
          if (ing.half === 'whole') {
            totalPrice = ing.price * ing.quantity;
          } else {
            totalPrice = (ing.price * ing.quantity);
          }
        } else {
          totalPrice = ing.price * ing.quantity;
        }
        
        return {
          id: ing.id,
          name: ing.name,
          price: ing.price,
          quantity: ing.quantity || 1,
          half: ing.half,
          sizeId: ing.sizeId,
          totalPrice: parseFloat(totalPrice.toFixed(2))
        };
      });

      // Prepare category object with size information
      const category: CartCategory = {
        id: this.selectedCategory.id,
        name: this.selectedCategory.name,
        default_size: this.selectedSize?.id || null,
        has_sizes: this.selectedCategory.has_sizes,
        default_price: this.selectedCategory.default_price,
        avatar: this.selectedCategory.avatar
      };

      // Add size info if selected
      if (this.selectedSize) {
        category.selected_size = {
          id: this.selectedSize.id,
          name: this.selectedSize.name,
          price: this.selectedSize.price
        };
      }

      // Prepare the new item in the exact format matching your pattern
      const newItem = {
        id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Generate unique ID
        type: 'custom', // Add type to identify custom products
        name: `Custom ${this.selectedCategory.name}`,
        avatar: this.selectedCategory.avatar,
        offer: null, // Custom products might not have offers
        rate: 0,
        
        // Custom product specific data
        customProducts: {
          category: category,
          subCategory: {
            id: this.selectedSubCategory.id,
            name: this.selectedSubCategory.name,
            avatar: this.selectedSubCategory.avatar
          },
          ingredients: ingredients,
          isHalfAndHalf: this.isHalfAndHalfMode,
          totalPrice: parseFloat(itemPrice.toFixed(2))
        },
        
        // For compatibility with your existing cart structure
        quantity: this.productQuantity,
        category_name: this.selectedCategory.name || 'Custom',
        
        // Additional metadata
        createdAt: new Date().toISOString(),
        selectedSize: this.selectedSize ? {
          id: this.selectedSize.id,
          name: this.selectedSize.name,
          price: this.selectedSize.price
        } : null,
        
        // Add these to match your existing pattern's structure
        selectedTypeId: this.selectedSubCategory?.id || null,
        selectedExtraId: ingredients, // Store ingredients here for compatibility
        selectedFreeSides: [] // Custom products might not have free sides
      };

      // Check if same custom product exists with same category, subcategory, ingredients, and size
      const existingItemIndex = cartItems.findIndex(item => {
        // Only compare custom products
        if (item.type !== 'custom') return false;
        
        // Check if same category
        if (item.customProducts?.category?.id !== newItem.customProducts.category.id) return false;
        
        // Check if same subcategory
        if (item.customProducts?.subCategory?.id !== newItem.customProducts.subCategory.id) return false;
        
        // Check if same size
        const itemSizeId = item.customProducts?.category?.selected_size?.id || null;
        const newSizeId = newItem.customProducts.category.selected_size?.id || null;
        if (itemSizeId !== newSizeId) return false;
        
        // Check if same half&half mode
        if (item.customProducts?.isHalfAndHalf !== newItem.customProducts.isHalfAndHalf) return false;
        
        // Compare ingredients (check if they have the same ingredients with same halves)
        const areIngredientsEqual = this.areCustomIngredientsEqual(
          item.customProducts?.ingredients || [], 
          newItem.customProducts.ingredients
        );
        
        return areIngredientsEqual;
      });

      if (existingItemIndex > -1) {
        // Update quantity if same custom product exists
        cartItems[existingItemIndex].quantity = newItem.quantity;
        this.alert.success(`Quantity updated (${cartItems[existingItemIndex].quantity})`);
      } else {
        // Add as new item
        cartItems.push(newItem);
        this.alert.success('Added to cart');
      }

      // Save updated cart to sessionStorage
      sessionStorage.setItem('customCartItems', JSON.stringify(cartItems));
      
      // Update cart count (optional)
      this.updateCartCount(cartItems);
      
      console.log('Updated guest cart:', cartItems);
      this.cartLoading = false;

    } catch (error) {
      console.error('Error handling guest cart:', error);
      this.cartLoading = false;
      this.alert.danger('Error adding to cart!');
    }
  }

  // Helper method to compare custom ingredients
  private areCustomIngredientsEqual(ingredients1: any[], ingredients2: any[]): boolean {
    if (ingredients1.length !== ingredients2.length) return false;
    
    // Sort both arrays by id and half for consistent comparison
    const sorted1 = [...ingredients1].sort((a, b) => {
      if (a.id !== b.id) return a.id - b.id;
      return (a.half || '').localeCompare(b.half || '');
    });
    
    const sorted2 = [...ingredients2].sort((a, b) => {
      if (a.id !== b.id) return a.id - b.id;
      return (a.half || '').localeCompare(b.half || '');
    });
    
    // Compare each ingredient
    return sorted1.every((ing, index) => {
      const ing2 = sorted2[index];
      return ing.id === ing2.id && 
            ing.half === ing2.half && 
            ing.quantity === ing2.quantity;
    });
  }

  // Helper method to update cart count in navbar
  private updateCartCount(cartItems: any[]): void {
    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    
    // You can emit this through a service or store in localStorage
    sessionStorage.setItem('cartCount', totalItems.toString());
    
    // Dispatch a custom event that your navbar can listen to
    window.dispatchEvent(new CustomEvent('cartUpdated', { 
      detail: { count: totalItems }
    }));
  }
  resetBuilder(): void {
    this.selectedCategory = null;
    this.selectedSubCategory = null;
    this.selectedSize = null;
    this.subCategories = [];
    this.topics = [];
    this.selectedIngredients = [];
    this.isHalfAndHalfMode = false;
    this.selectedHalf = 'whole';
    this.productQuantity = 1;
    this.hideQuantityInput();
  }

  getCurrentIngredientPrice(ingredient: any): number {
    if (!this.selectedSize) {
      return ingredient.originalPrice || ingredient.price;
    }
    
    // FIRST: Try to find the topic in the permanent map
    const permanentTopic = this.allTopicsMap.get(ingredient.id);
    if (permanentTopic && permanentTopic.sizes && permanentTopic.sizes.length > 0) {
      const sizePrice = permanentTopic.sizes.find(s => s.id === this.selectedSize.id);
      if (sizePrice) {
        return sizePrice.price;
      }
    }
    
    // SECOND: Try the size-specific map
    const sizePriceMap = this.topicSizeMap.get(`topic_${ingredient.id}`);
    if (sizePriceMap && sizePriceMap.has(this.selectedSize.id)) {
      return sizePriceMap.get(this.selectedSize.id) || ingredient.originalPrice || ingredient.price;
    }
    
    // THIRD: Try current topics as fallback
    const currentTopic = this.topics.find(t => t.id === ingredient.id);
    if (currentTopic && currentTopic.sizes && currentTopic.sizes.length > 0) {
      const sizePrice = currentTopic.sizes.find(s => s.id === this.selectedSize.id);
      if (sizePrice) {
        return sizePrice.price;
      }
    }
    
    // FINAL: Return stored price
    // console.log('No size price found, using original:', ingredient.originalPrice || ingredient.price);
    return ingredient.originalPrice || ingredient.price;
  }

  // Helper method to get topic name even when not in current topics
  getTopicName(ingredient: any): string {
    const topic = this.allTopicsMap.get(ingredient.id);
    return topic ? topic.name : ingredient.name;
  }

  // Reset maps when changing category
  selectCategory(category: any): void {
    this.selectedCategory = category;
    this.selectedSubCategory = null;
    this.selectedSize = null;
    this.subCategories = [];
    this.topics = [];
    this.selectedIngredients = [];
    this.productQuantity = 1;
    
    // Clear permanent storage when changing category
    this.allTopicsMap.clear();
    this.topicSizeMap.clear();
    
    if (category.name?.toLowerCase() === 'pizza') {
      this.isHalfAndHalfMode = false;
      this.selectedHalf = 'whole';
    } else {
      this.isHalfAndHalfMode = false;
      this.selectedHalf = 'whole';
    }
    
    this.hideQuantityInput();
    this.loadSubCategories(category.id);
  }


// Get the size name for an ingredient (for display)
  getIngredientSizeName(ingredient: any): string {
    if (!this.selectedSize) return '';
    
    const topic = this.topics.find(t => t.id === ingredient.id);
    if (topic && topic.sizes && topic.sizes.length > 0) {
        const size = topic.sizes.find(s => s.id === this.selectedSize.id);
        return size ? size.name : '';
    }
    return '';
  }

  // Update calculateItemPrice to use dynamic pricing
  calculateItemPrice(): number {
    const basePrice = this.getBasePrice();
    
    if (this.isHalfAndHalfMode) {
        // Whole ingredients
        const wholeIngredientsPrice = this.getIngredientsByHalf('whole')
            .reduce((sum, ing) => sum + (this.getCurrentIngredientPrice(ing) * (ing.quantity || 1)), 0);
        
        // Half ingredients (50% price for half portions)
        const leftHalfPrice = this.getIngredientsByHalf('left')
            .reduce((sum, ing) => sum + (this.getCurrentIngredientPrice(ing) * (ing.quantity || 1)), 0);
        
        const rightHalfPrice = this.getIngredientsByHalf('right')
            .reduce((sum, ing) => sum + (this.getCurrentIngredientPrice(ing) * (ing.quantity || 1)), 0);
        
        return basePrice + wholeIngredientsPrice + leftHalfPrice + rightHalfPrice;
    }
    
    // Normal mode
    const ingredientsPrice = this.selectedIngredients.reduce(
        (sum, ing) => sum + (this.getCurrentIngredientPrice(ing) * (ing.quantity || 1)), 0
    );
    return basePrice + ingredientsPrice;
  }
}